import React, {
  useState,
  useEffect,
  useCallback,
  type ReactElement,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Spinner } from '@pushwoosh/dumb-components';
import { Horizontal } from '@pushwoosh/kit-helpers';

import { useEnvironment } from '~/src/contexts/environment';
import { useLang } from '~/src/polyglot';

import { InviteSignUp } from './InviteSignUp.view';

export function InviteSignUpWithSSO(): ReactElement {
  const navigate = useNavigate();
  const { token = '' } = useParams();
  const { getLinkByName } = useEnvironment();
  const lang = useLang();

  const [inviteEmail, setInviteEmail] = useState('');
  const [isLoadingInvite, setIsLoadingInvite] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const acceptInvite = useCallback(async () => {
    const payload = new URLSearchParams();
    payload.append('code', token);

    try {
      const res = await fetch(
        '/api/accept-invite',
        {
          method: 'POST',
          body: payload,
        },
      );

      if (res.status !== 200) {
        const { message } = await res.json();
        setErrorMessage(message);
        return;
      }

      navigate(getLinkByName('selectAccount'));
    } catch (error: any) {
      setErrorMessage(lang({ message: 'INVITE.ERROR.ACCEPT_FAILED' }));
    }
  }, [token, navigate, getLinkByName, lang]);

  const fetchData = useCallback(async () => {
    setIsLoadingInvite(true);

    try {
      const payload = new URLSearchParams();
      payload.append('code', token);

      const res = await fetch(
        '/api/validate-invite',
        {
          method: 'POST',
          body: payload,
        },
      );

      if (res.status !== 200) {
        navigate(getLinkByName('login'));
        return;
      }

      const { validity, must, email } = await res.json();

      if (!validity) {
        navigate(getLinkByName('login'));
        return;
      }

      if (must === 'login') {
        navigate(`${getLinkByName('login')}?invite=${token}`);
        return;
      }

      if (must === 'accept') {
        await acceptInvite();
        return;
      }

      if (must === 'register') {
        setInviteEmail(email);
      }
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsLoadingInvite(false);
    }
  }, [token, navigate, getLinkByName, acceptInvite]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, fetchData]);

  if (isLoadingInvite) {
    return (
      <Horizontal $alignItems="center" $justifyContent="center">
        <Spinner size="medium" />
      </Horizontal>
    );
  }

  return (
    <InviteSignUp
      email={inviteEmail}
      inviteCode={token}
      globalError={errorMessage}
    />
  );
}
